import { gql } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { apolloClient } from "../..";
import EmptyStates from "../EmptyStates/EmptyStates";
import emptyTags from "../../assets/EmptyStates/empty_tags.svg";
import ListViewRows from "../../components/ListView/ListViewRows/ListViewRows";
import ListViewHeader from "../../components/ListView/ListViewHeader/ListViewHeader";
import AddEditTagModal from "./AddEditTagModal/AddEditTagModal";
import { Spinner } from "react-bootstrap";
import { notify } from "../Notify/Notify";
import DeleteModal from "../DeleteModal/DeleteModal";
import TagFilter from "./TagFilter.js/TagFilter";
import { isDateBetween, isDateEqual, isDateGreater } from "../../common/DateUtils";

// Styles
import "./TagsManagement.scss";

const filterTags = (listTags, searchString, filterCategories) => {
  if (listTags) {
    let filteredTags = [...listTags];
    if (searchString.trim() !== "") {
      filteredTags = filteredTags.filter((listTag) =>
        listTag.name.toLowerCase().includes(searchString.trim().toLowerCase())
      );
    }
    if (filterCategories) {
      filterCategories.map((filter) => {
        if (filter.name === "Category name") {
          const selectedOptionNames = filter.options.filter((option) => option.isSelected).map((option) => option.name);
          if (selectedOptionNames.length > 0) {
            filteredTags = filteredTags.filter((filterTag) => selectedOptionNames.includes(filterTag.category.name));
          }
        }

        if (filter.name === "Status") {
          if (filter.selectedValue) {
            filteredTags = filteredTags.filter((filterTag) =>
              filter.selectedValue === "Active" ? filterTag.isActive === true : filterTag.isActive === false
            );
          }
        }

        if (filter.name === "Created Date") {
          if (filter.selectedStartValue) {
            if (filter.selectedMatrix === "Between") {
              filteredTags = filteredTags.filter((filterTag) =>
                isDateBetween(filter.selectedStartValue, filter.selectedEndValue, filterTag.createdTs)
              );
            } else if (filter.selectedMatrix === "On") {
              filteredTags = filteredTags.filter((filterTag) =>
                isDateEqual(filter.selectedStartValue, filterTag.createdTs)
              );
            } else if (filter.selectedMatrix === "After") {
              filteredTags = filteredTags.filter((filterTag) =>
                isDateGreater(filter.selectedStartValue, filterTag.createdTs)
              );
            } else if (filter.selectedMatrix === "Before") {
              filteredTags = filteredTags.filter((filterTag) =>
                isDateGreater(filterTag.createdTs, filter.selectedStartValue)
              );
            }
          }
        }
        return null;
      });
    }
    return filteredTags;
  } else {
    return [];
  }
};

const TagsManagement = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [listTags, setListTags] = useState(null);
  const [listCategories, setListCategories] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchString, setSearchString] = useState("");
  const [filterCategories, setFilterCategories] = useState(null);
  const [filteredListTags, setFilteredListTags] = useState(listTags);

  const headings = ["Tag Name", "Status", "Created On", "Category name"];
  const columns = {
    isTagManagement: true,
    isActive: true,
    isCreatedOn: true,
    isCategoryName: true,
    isDropDown: true,
  };

  const loadTags = () => {
    apolloClient
      .query({
        query: gql`
          query {
            listTags {
              id
              name
              isActive
              createdTs
              modifiedTs
              category {
                id
                name
              }
            }
          }
        `,
        variables: {},
      })
      .then((result) => {
        const { listTags } = result.data;
        setIsLoading(false);
        setListTags(listTags);
      })
      .catch((e) => {
        setIsLoading(false);
        setListTags([]);
      });
  };

  const handleEdit = (tag) => {
    setSelectedTag(tag);
    setIsModalOpen(true);
  };

  const handleActivateDeactivate = async (tag) => {
    try {
      // Activate/Deactivate Tag
      const variables = {
        tagId: tag.id,
        isActivate: !tag.isActive,
      };
      const result = await apolloClient.mutate({
        variables: variables,
        mutation: gql`
          mutation ActivateTag($tagId: UUID!, $isActivate: Boolean!) {
            activateTag(tagId: $tagId, isActivate: $isActivate) {
              success
            }
          }
        `,
      });
      const { success } = result.data.activateTag;
      if (success) {
        // Update the tag list
        setListTags((listTags) =>
          [...listTags].map((listTag) => {
            if (listTag.id === tag.id) {
              return { ...listTag, isActive: !listTag.isActive };
            } else {
              return listTag;
            }
          })
        );

        // Show success message
        const title = "Success";
        const detail = `Successfully ${tag.isActive ? "Deactivated" : "Activated"}!`;
        const type = "success";
        notify(title, detail, type);
      }
    } catch (e) {
      const title = "Error";
      const detail = e.message ?? "oops! Something went wrong";
      const type = "error";
      notify(title, detail, type);
    }
  };

  const handleDelete = (tag) => {
    setSelectedTag(tag);
    setIsDeleteModalOpen(true);
  };

  const deleteTag = async () => {
    try {
      // Delete Tag
      const variables = {
        tagId: selectedTag.id,
      };
      const result = await apolloClient.mutate({
        variables: variables,
        mutation: gql`
          mutation DeleteTag($tagId: UUID!) {
            deleteTag(tagId: $tagId) {
              success
            }
          }
        `,
      });
      const { success } = result.data.deleteTag;
      if (success) {
        // Update the tag list
        setListTags((listTags) => [...listTags].filter((listTag) => listTag.id !== selectedTag.id));
        setIsDeleteModalOpen(false);

        // Show success message
        const title = "Success";
        const detail = "Successfully deleted!";
        const type = "success";
        notify(title, detail, type);
      }
    } catch (e) {
      const title = "Error";
      const detail = e.message ?? "oops! Something went wrong";
      const type = "error";
      notify(title, detail, type);
    }
  };

  const handleFilterTags = () => {
    setFilteredListTags(filterTags(listTags, searchString, filterCategories));
  };

  useEffect(() => {
    // Load tags
    loadTags();

    // Load categories
    apolloClient
      .query({
        query: gql`
          query {
            listCategories {
              id
              name
            }
          }
        `,
        variables: {},
      })
      .then((result) => {
        const { listCategories } = result.data;
        setListCategories(listCategories);
      })
      .catch((e) => {
        setListCategories([]);
      });
  }, []);

  useEffect(() => {
    setFilteredListTags(filterTags(listTags, searchString, filterCategories));
  }, [searchString, listTags, filterCategories]);

  useEffect(() => {
    //  Clear state
    if (!isModalOpen && !isDeleteModalOpen) {
      setSelectedTag(null);
    }
  }, [isModalOpen, isDeleteModalOpen]);

  return (
    <div className="tags-management-wraper">
      {listTags && listTags.length > 0 ? (
        <div className="tags-management-header d-flex">
          <div className="search-filter-container">
            <div className="search-box mr-2">
              <input
                type="text"
                maxLength="50"
                placeholder="&#xF002;  Search"
                className="search-placeholder set-width"
                value={searchString}
                onChange={(e) => {
                  setSearchString(e.target.value);
                  handleFilterTags();
                }}
              />
            </div>
            {listCategories ? (
              <TagFilter {...{ listCategories, handleFilterTags, filterCategories, setFilterCategories }} />
            ) : null}
          </div>

          <AddEditTagModal
            {...{
              isModalOpen,
              setIsModalOpen,
              listTags,
              listCategories,
              setListCategories,
              setListTags,
              selectedTag,
              loadTags,
              isDocumentView: false,
            }}
          />

          <DeleteModal
            {...{
              title: "Delete Tag",
              description: (
                <>
                  Are you sure you want to delete <b>{selectedTag ? selectedTag.name : ""} </b>
                  tag?
                </>
              ),
              show: isDeleteModalOpen,
              handleClose: () => {
                setIsDeleteModalOpen(false);
              },
              handleDelete: deleteTag,
            }}
          />
        </div>
      ) : null}
      <div className="table-responsive tags-management-list">
        {isLoading ? (
          <div className="progress-icon" style={{ textAlign: "center" }}>
            <Spinner animation="border" role="status" />
          </div>
        ) : filteredListTags && filteredListTags.length > 0 ? (
          <table className="table table-hover">
            <thead>
              <tr className="folderlist-header table-borderless">
                {headings.map((headKey) => (
                  <ListViewHeader key={headKey} columnName={headKey} checked={false} />
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredListTags.map((tag, index) => (
                <ListViewRows
                  key={tag.id}
                  item={tag}
                  index={index}
                  isColumns={columns}
                  size={true}
                  setStatus={() => {}}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  handleActivateDeactivate={handleActivateDeactivate}
                  isTagManagement={true}
                />
              ))}
            </tbody>
          </table>
        ) : listTags && listTags.length === 0 ? (
          // No tags screen
          <div className="no-tags is-size-h2 bold">
            <EmptyStates
              imgSrc={emptyTags}
              emptyStateTitle="Create entities to tag your documents"
              emptyStateButtons={[]}
            />
            <AddEditTagModal
              {...{
                isModalOpen,
                setIsModalOpen,
                listTags,
                listCategories,
                setListCategories,
                setListTags,
                selectedTag,
                loadTags,
                isDocumentView: false,
              }}
            />
          </div>
        ) : (
          <div className="no-tags is-size-h2 bold">
            <EmptyStates
              imgSrc={emptyTags}
              emptyStateTitle="No results found !"
              description="Please try again using different filters or search terms"
              emptyStateButtons={[]}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TagsManagement;
